import { type PropsWithChildren } from 'react';
import { RefreshCcw, ShieldAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { BrandLogo } from '@/components/branding/brand-logo';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { isInvitationAcceptPathname } from '@/lib/invitation-routes';
import { useAppBootstrap } from '@/providers/useAppBootstrap';

const LOADING_MESSAGE = 'Getting everything ready';

function resolveErrorMessage(error: unknown, fallback: string) {
    if (error instanceof Error && error.message.trim().length > 0) {
        return error.message;
    }

    return fallback;
}

function BootstrapLogoSpinner() {
    return (
        <div
            className='animate-bootstrap-logo-pulse will-change-transform'
            aria-hidden='true'
            data-testid='bootstrap-logo-spinner'
        >
            <BrandLogo showLabel={false} className='size-14' />
        </div>
    );
}

export function AppBootstrapGate({ children }: PropsWithChildren) {
    const { status, error, retry } = useAppBootstrap();
    const { t } = useTranslation();
    const bypassBootstrapGate = typeof window !== 'undefined' && isInvitationAcceptPathname(window.location.pathname);

    if (bypassBootstrapGate && status !== 'auth-initializing' && status !== 'error') {
        return children;
    }

    if (
        status === 'idle' ||
        status === 'auth-initializing' ||
        status === 'awaiting-auth' ||
        status === 'bootstrapping'
    ) {
        return (
            <div
                className='flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-6 py-10 text-center'
                role='status'
                aria-live='polite'
                aria-label={LOADING_MESSAGE}
                data-testid='app-bootstrap-gate'
            >
                <BootstrapLogoSpinner />
                <p className='text-xl font-medium text-foreground sm:text-2xl'>{LOADING_MESSAGE}</p>
            </div>
        );
    }

    if (status === 'error') {
        return (
            <div
                className='relative flex min-h-screen items-center justify-center overflow-hidden bg-background px-6 py-10'
                data-testid='app-bootstrap-gate'
            >
                <div className='absolute inset-0 bg-[radial-gradient(circle_at_top,_color-mix(in_oklab,var(--color-destructive)_14%,transparent),transparent_55%)]' />
                <Card variant='startup' className='relative z-10 w-full max-w-lg'>
                    <CardHeader density='comfortable'>
                        <BrandLogo className='justify-center' />
                        <div className='space-y-2 text-center'>
                            <div className='mx-auto flex size-12 items-center justify-center rounded-full bg-destructive/12 text-destructive'>
                                <ShieldAlert className='size-6' aria-hidden='true' />
                            </div>
                            <CardTitle>{t('startup.errorTitle')}</CardTitle>
                            <CardDescription>{t('startup.errorDescription')}</CardDescription>
                        </div>
                    </CardHeader>
                    <CardContent spacing='comfortable'>
                        <div
                            className='rounded-lg border border-destructive/20 bg-destructive/6 p-3 text-sm text-destructive'
                            role='alert'
                        >
                            {resolveErrorMessage(error, t('startup.errorFallback'))}
                        </div>
                        <div className='flex flex-col items-center gap-3 sm:flex-row sm:justify-center'>
                            <Button onClick={() => void retry()}>
                                <RefreshCcw aria-hidden='true' />
                                {t('startup.retry')}
                            </Button>
                            <p className='text-center text-sm text-muted-foreground'>{t('startup.retryHint')}</p>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return children;
}
